// Rule-based, client-side grocery aisle classifier. No network, no storage,
// no model: a small keyword map plus a few phrase overrides.

export const AISLES = [
  "Produce",
  "Dairy & Eggs",
  "Meat & Seafood",
  "Bakery",
  "Frozen",
  "Pantry",
  "Household",
  "Other",
] as const;

export const AUTO_AISLE = "Auto";

const normalize = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9\s'-]/g, " ")
    .replace(/['-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

function singular(w: string): string {
  if (w.length <= 3) return w;
  if (/(ss|us|is)$/.test(w)) return w;
  if (w.endsWith("ies")) return w.slice(0, -3) + "y";
  if (/(oes|ches|shes|xes)$/.test(w)) return w.slice(0, -2);
  if (w.endsWith("s")) return w.slice(0, -1);
  return w;
}

// Phrase rules run first (checked as whole-word substrings of the singularized,
// normalized name). Order matters: the first match wins.
const PHRASES: [string, string][] = [
  ["frozen", "Frozen"],
  ["ice cream", "Frozen"],
  ["popsicle", "Frozen"],
  ["gelato", "Frozen"],
  ["tater tot", "Frozen"],
  ["french fry", "Frozen"],
  ["fish stick", "Frozen"],
  ["canned", "Pantry"],
  ["can of", "Pantry"],
  ["peanut butter", "Pantry"],
  ["almond butter", "Pantry"],
  ["apple butter", "Pantry"],
  ["cocoa butter", "Household"],
  ["butter bean", "Pantry"],
  ["coconut milk", "Pantry"],
  ["evaporated milk", "Pantry"],
  ["condensed milk", "Pantry"],
  ["milk chocolate", "Pantry"],
  ["soy milk", "Dairy & Eggs"],
  ["oat milk", "Dairy & Eggs"],
  ["almond milk", "Dairy & Eggs"],
  ["cream cheese", "Dairy & Eggs"],
  ["sour cream", "Dairy & Eggs"],
  ["whipped cream", "Dairy & Eggs"],
  ["cream of", "Pantry"],
  ["egg noodle", "Pantry"],
  ["egg roll", "Frozen"],
  ["bread crumb", "Pantry"],
  ["breadcrumb", "Pantry"],
  ["hot dog bun", "Bakery"],
  ["hamburger bun", "Bakery"],
  ["burger bun", "Bakery"],
  ["english muffin", "Bakery"],
  ["hot dog", "Meat & Seafood"],
  ["chicken broth", "Pantry"],
  ["chicken stock", "Pantry"],
  ["beef broth", "Pantry"],
  ["vegetable broth", "Pantry"],
  ["chicken soup", "Pantry"],
  ["tomato sauce", "Pantry"],
  ["tomato paste", "Pantry"],
  ["pasta sauce", "Pantry"],
  ["sun dried tomato", "Pantry"],
  ["orange juice", "Pantry"],
  ["apple juice", "Pantry"],
  ["lemon juice", "Pantry"],
  ["lime juice", "Pantry"],
  ["black bean", "Pantry"],
  ["kidney bean", "Pantry"],
  ["green bean", "Produce"],
  ["sweet potato", "Produce"],
  ["bell pepper", "Produce"],
  ["black pepper", "Pantry"],
  ["peppercorn", "Pantry"],
  ["hot sauce", "Pantry"],
  ["soy sauce", "Pantry"],
  ["baking soda", "Pantry"],
  ["baking powder", "Pantry"],
  ["dish soap", "Household"],
  ["hand soap", "Household"],
  ["body wash", "Household"],
  ["trash bag", "Household"],
  ["garbage bag", "Household"],
  ["paper towel", "Household"],
  ["toilet paper", "Household"],
  ["laundry detergent", "Household"],
  ["fabric softener", "Household"],
  ["aluminum foil", "Household"],
  ["plastic wrap", "Household"],
  ["cling film", "Household"],
  ["zip lock", "Household"],
  ["sandwich bag", "Household"],
  ["light bulb", "Household"],
  ["cat food", "Household"],
  ["dog food", "Household"],
  ["cat litter", "Household"],
  ["peanut", "Pantry"],
];

const KEYWORDS: Record<string, string[]> = {
  Produce: [
    "banana", "apple", "orange", "lemon", "lime", "grape", "strawberry", "blueberry", "raspberry",
    "blackberry", "berry", "cherry", "peach", "pear", "plum", "mango", "pineapple", "watermelon",
    "melon", "cantaloupe", "kiwi", "papaya", "pomegranate", "grapefruit", "tangerine", "clementine",
    "avocado", "lettuce", "spinach", "kale", "arugula", "cabbage", "broccoli", "cauliflower",
    "carrot", "celery", "cucumber", "tomato", "potato", "onion", "garlic", "ginger", "shallot",
    "scallion", "leek", "zucchini", "squash", "pumpkin", "eggplant", "mushroom", "asparagus",
    "corn", "radish", "beet", "turnip", "pepper", "jalapeno", "cilantro", "parsley", "basil",
    "mint", "dill", "rosemary", "thyme", "herb", "salad", "fruit", "vegetable", "veggie",
    "produce", "coconut", "fig", "date", "artichoke", "brussels", "bok", "chard", "okra", "yam",
  ],
  "Dairy & Eggs": [
    "milk", "egg", "butter", "cheese", "yogurt", "yoghurt", "cream", "creamer", "cheddar",
    "mozzarella", "parmesan", "feta", "brie", "gouda", "swiss", "provolone", "ricotta", "cottage",
    "kefir", "margarine", "ghee", "paneer", "custard", "dairy", "half",
  ],
  "Meat & Seafood": [
    "chicken", "beef", "pork", "turkey", "lamb", "steak", "bacon", "sausage", "ham", "salami",
    "pepperoni", "veal", "duck", "brisket", "ribs", "rib", "roast", "mince", "meatball", "patty",
    "salmon", "tuna", "shrimp", "prawn", "cod", "tilapia", "fish", "crab", "lobster", "scallop",
    "oyster", "mussel", "clam", "sardine", "anchovy", "seafood", "meat", "drumstick", "wing",
    "thigh", "fillet", "filet", "jerky",
  ],
  Bakery: [
    "bread", "bagel", "tortilla", "roll", "bun", "baguette", "croissant", "muffin", "donut",
    "doughnut", "pita", "naan", "cake", "pie", "pastry", "cookie", "brownie", "loaf", "sourdough",
    "cooky", "browny", "ciabatta", "focaccia", "scone", "danish", "wrap", "cupcake", "toast",
  ],
  Frozen: ["popsicle", "sorbet", "sherbet", "edamame", "nugget", "waffle", "pierogi", "dumpling"],
  Pantry: [
    "rice", "pasta", "spaghetti", "macaroni", "noodle", "penne", "lasagna", "cereal", "oat",
    "oatmeal", "granola", "flour", "sugar", "salt", "oil", "vinegar", "honey", "syrup", "jam",
    "jelly", "ketchup", "mustard", "mayo", "mayonnaise", "sauce", "salsa", "spice", "cumin",
    "paprika", "cinnamon", "oregano", "seasoning", "coffee", "tea", "cocoa", "juice", "soda",
    "water", "cracker", "chip", "pretzel", "popcorn", "nut", "almond", "cashew", "walnut",
    "pecan", "pistachio", "raisin", "bean", "lentil", "chickpea", "quinoa", "couscous", "soup",
    "broth", "stock", "tofu", "tahini", "hummus", "pickle", "olive", "can", "candy", "chocolate",
    "snack", "bar", "cornstarch", "yeast", "vanilla", "wine", "beer", "lemonade", "kombucha",
    "oatmeal", "pancake", "breadcrumb", "tortilla chip", "ramen", "sriracha", "teriyaki",
  ],
  Household: [
    "detergent", "bleach", "sponge", "soap", "shampoo", "conditioner", "toothpaste", "toothbrush",
    "deodorant", "tissue", "napkin", "foil", "battery", "bulb", "cleaner", "wipe", "diaper",
    "lotion", "floss", "mouthwash", "razor", "sunscreen", "candle", "mop", "broom", "disinfectant",
    "dishwasher", "tampon",
  ],
};

const KEYWORD_INDEX = (() => {
  const m = new Map<string, string>();
  // Earlier aisles in this order win ties for words listed in more than one aisle.
  for (const aisle of ["Dairy & Eggs", "Meat & Seafood", "Bakery", "Frozen", "Household", "Pantry", "Produce"]) {
    for (const w of KEYWORDS[aisle]) if (!m.has(w)) m.set(w, aisle);
  }
  return m;
})();

/** Returns the best-guess aisle for a free-text item name, or "Other". */
export function classifyAisle(rawName: string): string {
  const norm = normalize(rawName);
  if (!norm) return "Other";
  const words = norm.split(" ").map(singular);
  const padded = ` ${words.join(" ")} `;

  for (const [phrase, aisle] of PHRASES) {
    if (padded.includes(` ${phrase} `)) {
      return aisle;
    }
  }
  // Last word is usually the head noun ("whole wheat bread"), so check it first.
  for (let i = words.length - 1; i >= 0; i--) {
    const aisle = KEYWORD_INDEX.get(words[i]);
    if (aisle) return aisle;
  }
  return "Other";
}

export interface GroceryItem {
  id: number;
  name: string;
  aisle: string;
  qty: string;
  checked: boolean;
}

const dedupeKey = (name: string) =>
  normalize(name)
    .split(" ")
    .map(singular)
    .join(" ");

function parseQty(q: string): { n: number; unit: string } | null {
  const m = q.trim().match(/^(\d+(?:\.\d+)?)\s*([a-zA-Z]*)$/);
  return m ? { n: parseFloat(m[1]), unit: m[2].toLowerCase() } : null;
}

export function mergeQty(a: string, b: string): string {
  const pa = parseQty(a);
  const pb = parseQty(b);
  if (pa && pb && pa.unit === pb.unit) {
    const n = Math.round((pa.n + pb.n) * 100) / 100;
    return pa.unit ? `${n} ${pa.unit}` : `${n}`;
  }
  if (a.trim() === b.trim()) return a;
  return `${a} + ${b}`;
}

/** Merges items with the same (case/plural-insensitive) name, whatever their aisle. */
export function mergeDuplicates(items: GroceryItem[]): GroceryItem[] {
  const out: GroceryItem[] = [];
  const seen = new Map<string, number>();
  for (const it of items) {
    const key = dedupeKey(it.name);
    const at = seen.get(key);
    if (at === undefined) {
      seen.set(key, out.length);
      out.push({ ...it });
    } else {
      const kept = out[at];
      kept.qty = mergeQty(kept.qty, it.qty);
      kept.checked = kept.checked && it.checked;
    }
  }
  return out;
}

/**
 * Adds an item, resolving "Auto" to a classified aisle and merging into an
 * existing entry with the same name instead of creating a duplicate.
 */
export function addGroceryItem(
  items: GroceryItem[],
  next: { id: number; name: string; aisle: string; qty: string },
): GroceryItem[] {
  const name = next.name.trim();
  if (!name) return items;
  const aisle = next.aisle === AUTO_AISLE ? classifyAisle(name) : next.aisle;
  return mergeDuplicates([...items, { id: next.id, name, aisle, qty: next.qty || "1", checked: false }]);
}
